import i18n from './i18n'

/**
 * The session lives in an HttpOnly cookie, so every call just has to send credentials — there is
 * no token here to steal, and nothing to put in local storage.
 */
function readCookie(name) {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

/**
 * A file, sent as multipart. The browser sets the boundary itself, so no Content-Type is given
 * here — writing one by hand produces a body the server cannot parse.
 */
async function upload(path, file) {
  const form = new FormData()
  form.append('file', file)

  const token = readCookie('XSRF-TOKEN')

  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : undefined,
    body: form,
  })

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try {
      const problem = await response.json()
      detail = problem.title ?? detail
    } catch {
      // Not a problem document; the status is all we have.
    }
    throw new Error(detail)
  }

  return response.json()
}

/**
 * The double-submit token is bound to the identity it was issued for, so it goes stale when a
 * session ends — in this tab or another one. Rather than showing "missing or invalid request
 * token", which nobody can act on, a safe GET fetches a fresh pair and the call is tried once more.
 */
async function withFreshToken(retry) {
  await fetch('/api/system/config', { credentials: 'same-origin' })
  return retry()
}

async function request(path, { method = 'GET', body, retrying = false } = {}) {
  const headers = {}

  if (body) headers['Content-Type'] = 'application/json'

  // Double-submit: the secret half is an HttpOnly cookie the server set, this is the readable
  // half echoed back. Only same-origin script can read it, which is what a forged cross-site
  // request cannot do.
  if (method !== 'GET' && method !== 'HEAD') {
    const token = readCookie('XSRF-TOKEN')
    if (token) headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
  }

  let response

  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: body ? JSON.stringify(body) : undefined,
    })
  } catch {
    // The browser's own words for no network — "Failed to fetch" — are English and technical,
    // and every screen that shows an error shows its message. Said here once, in the owner's
    // language, rather than translated at two dozen places.
    const error = new Error(i18n.t('errors.unreachable'))
    error.unreachable = true
    throw error
  }

  if (response.status === 401) {
    const error = new Error('unauthenticated')
    error.unauthenticated = true
    throw error
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    let type = null

    try {
      const problem = await response.json()
      detail = problem.title ?? problem.detail ?? detail
      type = problem.type
    } catch {
      // Not a problem document; the status is all we have.
    }

    if (!retrying && type === 'https://dosegni.com/errors/antiforgery') {
      return withFreshToken(() => request(path, { method, body, retrying: true }))
    }

    // The type travels with the error so a screen can say the refusal in the owner's language;
    // the title stays as the message, for a refusal nobody has translated yet.
    const error = new Error(detail)
    error.type = type
    error.status = response.status
    throw error
  }

  return response.status === 204 ? null : response.json()
}

export const api = {
  config: () => request('/api/system/config'),
  version: () => request('/api/system/version'),
  me: () => request('/api/auth/me'),
  signInWithFacebook: (code) => request('/api/auth/facebook', { method: 'POST', body: { code } }),
  register: (body) => request('/api/auth/register', { method: 'POST', body }),
  login: (body) => request('/api/auth/login', { method: 'POST', body }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  logoutEverywhere: () => request('/api/auth/logout-everywhere', { method: 'POST' }),
  tenants: () => request('/api/auth/tenants'),
  verifyEmail: (token) => request('/api/account/verify', { method: 'POST', body: { token } }),
  resendVerification: () => request('/api/account/verify/resend', { method: 'POST' }),
  forgotPassword: (email) => request('/api/account/password/forgot', { method: 'POST', body: { email } }),
  resetPassword: (token, password) =>
    request('/api/account/password/reset', { method: 'POST', body: { token, password } }),
  switchTenant: (tenantId) => request(`/api/auth/tenants/${tenantId}/switch`, { method: 'POST' }),
  profile: () => request('/api/profile'),
  uploadProductImage: (productId, file) => upload(`/api/products/${productId}/image`, file),
  uploadLogo: (file) => upload('/api/profile/logo', file),

  // The business's own pictures, for businesses whose pictures are all they have.
  contentSets: () => request('/api/sets'),
  createContentSet: (about) => request('/api/sets', { method: 'POST', body: { about } }),
  uploadContentSetImage: (setId, file) => upload(`/api/sets/${setId}/image`, file),
  removeContentSetImage: (setId, url) =>
    request(`/api/sets/${setId}/image?url=${encodeURIComponent(url)}`, { method: 'DELETE' }),
  deleteContentSet: (setId) => request(`/api/sets/${setId}`, { method: 'DELETE' }),
  /** One generated clip in place of the slideshow. Queued: the answer comes back at once. */
  makeItMove: (postId) => request(`/api/posts/${postId}/move`, { method: 'POST' }),


  writePostFromSet: (setId, video) =>
    request(`/api/sets/${setId}/post?video=${video ? 'true' : 'false'}`, { method: 'POST' }),
  saveProfile: (body) => request('/api/profile', { method: 'PUT', body }),
  sites: () => request('/api/sites'),
  checkSite: (siteUrl) => request('/api/sites/check', { method: 'POST', body: { siteUrl } }),
  addSite: (siteUrl) => request('/api/sites', { method: 'POST', body: { siteUrl } }),
  ingestSite: (siteId) => request(`/api/sites/${siteId}/ingest`, { method: 'POST' }),
  posts: (status) => request(`/api/posts${status ? `?status=${status}` : ''}`),
  post: (postId) => request(`/api/posts/${postId}`),
  suggestPosts: (count, strategy) =>
    request('/api/posts/suggest', { method: 'POST', body: { count, strategy } }),
  editVariant: (postId, variantId, text, hashtags) =>
    request(`/api/posts/${postId}/variants/${variantId}`, { method: 'PUT', body: { text, hashtags } }),
  approvePost: (postId) => request(`/api/posts/${postId}/approve`, { method: 'POST' }),
  rejectPost: (postId) => request(`/api/posts/${postId}/reject`, { method: 'POST' }),
  regeneratePost: (postId) => request(`/api/posts/${postId}/regenerate`, { method: 'POST' }),

  /** One step back: the words, the levers and the pictures the post had before the last change. */
  undoPost: (postId) => request(`/api/posts/${postId}/undo`, { method: 'POST' }),

  /** Change the words of a post to what was asked, keeping every fact they state. */
  revisePost: (postId, scope, instruction) =>
    request(`/api/posts/${postId}/revise`, { method: 'POST', body: { scope, instruction } }),

  /** Ten more changes to one post's words, for a credit. */
  buyRevisions: (postId) => request(`/api/posts/${postId}/revise/allowance`, { method: 'POST' }),
  /**
   * Builds the post's picture. `ai` true draws one even though the shop has a photograph, false
   * puts the photograph back, and leaving it out keeps whatever the post was already told.
   */
  /** Where the words on the picture sit: a tap on the grid, one field per element. */
  designPost: (postId, patch) =>
    request(`/api/posts/${postId}/design`, { method: 'POST', body: patch }),

  /**
   * What is worth saying about a post before it is approved. Its own call because it reads the
   * drawn pixels: one picture for one post somebody has open, not every picture in a list.
   */
  checkPost: (postId) => request(`/api/posts/${postId}/check`),

  /**
   * Ordinary drawing happens in the request and comes back done. A premium one is queued and
   * answers 202: the picture lands a minute later and the sheet watches for it.
   */
  buildImage: (postId, ai = null, premium = false) => {
    // Premium implies drawing; the server treats it the same way, so the two can never disagree.
    const query = [ai === null ? null : `ai=${ai}`, premium ? 'premium=true' : null]
      .filter(Boolean)
      .join('&')

    return request(`/api/posts/${postId}/image${query ? `?${query}` : ''}`, { method: 'POST' })
  },
  deletePost: (postId) => request(`/api/posts/${postId}`, { method: 'DELETE' }),
  campaign: (message, history) => request('/api/posts/campaign', { method: 'POST', body: { message, history } }),
  /**
   * A line in the conversation. focusPostId is what the owner has open while they type — what
   * makes "цената горе" a sentence rather than a riddle. It stands in only where the designer
   * named no post itself, and never for approving or publishing one.
   */
  assistant: (message, marker = null, language = null, focusPostId = null) =>
    request('/api/posts/assistant', {
      method: 'POST',
      body: { message, marker, language, focusPostId },
    }),

  /** What is on sale, and a way to say which one. */
  plans: () => request('/api/billing/plans'),
  wantPlan: (planCode, credits = 1) =>
    request('/api/billing/interest', { method: 'POST', body: { planCode, credits } }),

  /**
   * Which layout, chosen by looking at three samples. The tap carries the turn the offer was made
   * on and the layout's key — never the campaign, which the server wrote down when it asked.
   */
  chooseLayout: (turnId, key, message) =>
    request('/api/posts/campaign/layout', { method: 'POST', body: { turnId, key, message } }),

  /** One credit for room to keep writing after too many drafts were thrown away. */
  buyDiscards: () => request('/api/posts/allowance/discards', { method: 'POST' }),

  /**
   * A story drawn for a post — the owner tapped how many pictures. Their words travel with it so
   * the conversation shows what they chose, in the language they chose it in.
   */
  storyboard: (postId, scenes, message) =>
    request(`/api/posts/${postId}/storyboard`, { method: 'POST', body: { scenes, message } }),

  /** The conversation as the server kept it — what makes a reopened chat carry on. */
  assistantTurns: () => request('/api/posts/assistant/turns'),
  /** Where the business stands, for the starting points the chat offers. */
  assistantState: () => request('/api/posts/assistant/state'),
  deleteSite: (siteId) => request(`/api/sites/${siteId}`, { method: 'DELETE' }),
  calendar: (from, to) => request(`/api/posts/calendar?from=${from}&to=${to}`),
  schedulePost: (postId, scheduledAtLocal) =>
    request(`/api/posts/${postId}/schedule`, { method: 'POST', body: { scheduledAtLocal } }),
  unschedulePost: (postId) => request(`/api/posts/${postId}/unschedule`, { method: 'POST' }),
  bulkPosts: (action, ids) => request('/api/posts/bulk', { method: 'POST', body: { action, ids } }),
  publishPost: (postId) => request(`/api/posts/${postId}/publish`, { method: 'POST' }),
  socialAccounts: () => request('/api/social/accounts'),
  availablePages: () => request('/api/social/available'),
  connectPages: (pageIds) => request('/api/social/connect', { method: 'POST', body: { pageIds } }),
  disconnectAccount: (accountId) => request(`/api/social/accounts/${accountId}`, { method: 'DELETE' }),
}

/**
 * Loads the Facebook JavaScript SDK on demand. Not in index.html: the portal has to render, and
 * say why, on a machine that cannot reach Facebook at all.
 */
export function loadFacebookSdk({ appId, graphVersion }) {
  if (window.FB) return Promise.resolve(window.FB)

  return new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB.init({ appId, version: graphVersion, xfbml: false, cookie: false })
      resolve(window.FB)
    }

    const script = document.createElement('script')
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.onerror = () => reject(new Error('facebook-sdk-unreachable'))
    document.head.appendChild(script)
  })
}

/**
 * Facebook Login for Business. `response_type: 'code'` is what keeps the app secret on the server:
 * the browser never sees an access token, only a one-time code the API exchanges.
 */
export function facebookLogin(FB, configId) {
  return new Promise((resolve, reject) => {
    FB.login(
      (response) => {
        if (response.code) resolve(response.code)
        else reject(new Error('cancelled'))
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
      },
    )
  })
}
